import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { startPayment } from '../../api/apiStorefront';
import { PUBLIC_URL } from '../../config';
import parseError from '../../utils/parseError';
import { trackPaymentFailure } from '../../utils/telemetry';

// Same allow-list of payment provider hosts the Checkout page enforces.
// Centralised here so future providers only need to be added in one place.
const TRUSTED_REDIRECT_HOSTS = [
    'accept.paymobsolutions.com',
    'accept.paymob.com',
    'pay.paymob.com',
    'oneclick.paymob.com',
];

const isTrustedRedirect = (urlStr) => {
    try {
        const u = new URL(urlStr);
        if (u.protocol !== 'https:') return false;
        return TRUSTED_REDIRECT_HOSTS.some(
            (host) => u.hostname === host || u.hostname.endsWith('.' + host)
        );
    } catch {
        return false;
    }
};

/**
 * Restart the payment flow for an existing pending order. Used from
 * /orders when a customer abandoned a checkout mid-Paymob and needs to
 * pick up where they left off without re-creating the order.
 *
 * Returns:
 *   resume(orderId, provider = 'paymob') → triggers POST /payments/start
 *     and either redirects to the provider or routes the customer to
 *     the result page (failed=1) so they can retry.
 *   isLoading → mirror of the in-flight request flag.
 */
export const useResumePayment = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const resume = async (orderId, provider = 'paymob') => {
        if (!orderId) return;
        setIsLoading(true);
        try {
            const res = await startPayment({
                order_id: orderId,
                provider,
                meta: {
                    return_url: `${PUBLIC_URL}/checkout/${orderId}/result`,
                    cancel_url: `${PUBLIC_URL}/checkout/${orderId}/result?cancelled=1`,
                },
            });

            if (!res.data?.success) {
                trackPaymentFailure('payment_resume_failed', {
                    orderId,
                    provider,
                    reason: res.data?.message || 'success_false',
                });
                toast.error(res.data?.message || t('checkout.paymentStartError'));
                window.location.assign(`/checkout/${orderId}/result?failed=1`);
                return;
            }

            const redirectUrl = res.data.data?.redirect_url;
            if (redirectUrl) {
                if (!isTrustedRedirect(redirectUrl)) {
                    trackPaymentFailure('payment_resume_redirect_untrusted', {
                        orderId,
                        provider,
                        reason: redirectUrl,
                    });
                    toast.error(t('checkout.paymentStartError'));
                    window.location.assign(`/checkout/${orderId}/result?failed=1`);
                    return;
                }
                window.location.href = redirectUrl;
                return;
            }

            // No redirect_url for an online provider — backend bug surface.
            trackPaymentFailure('payment_resume_no_redirect', {
                orderId,
                provider,
                reason: 'redirect_url_null',
            });
            toast.error(t('checkout.paymentStartError'));
            window.location.assign(`/checkout/${orderId}/result?failed=1`);
        } catch (err) {
            const status = err?.response?.status;
            trackPaymentFailure('payment_resume_failed', {
                orderId,
                provider,
                status,
                reason: err?.response?.data?.message || err?.message || 'network',
            });
            if (status === 429) {
                const retryAfter = Number(err?.response?.data?.meta?.retry_after);
                if (Number.isFinite(retryAfter) && retryAfter > 0) {
                    toast.error(t('checkout.throttleSeconds', { n: retryAfter }));
                } else {
                    toast.error(t('checkout.throttle'));
                }
                return;
            }
            toast.error(parseError(err) || t('checkout.paymentStartError'));
        } finally {
            setIsLoading(false);
        }
    };

    return { resume, isLoading };
};

export default useResumePayment;
